import { useInstructionsStore } from "store/aiAgent.store"
import { useUpdateInstructionStatus } from "hooks/api/aiAgent/aiAgent.hook"
import { useMessage } from "hooks/message.hook"
import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import type { MenuProps } from "antd"
import type { IInstructionsTable } from "types/table.type"

type Props = {
	setEditInstructionModalOpen?: () => void
	onDeleteClick: (id: number) => void
}

export const InstructionsTableUtils = ({ setEditInstructionModalOpen, onDeleteClick }: Props) => {
	const queryClient = useQueryClient()
	const isInstructionsFetching = queryClient.isFetching({ queryKey: ["getInstructions"] }) > 0

	const { setInstruction } = useInstructionsStore()
	const { error: showErrorMessage } = useMessage()
	const [pendingQuestionId, setPendingQuestionId] = useState<number | null>(null)
	const { mutate: updateInstructionsStatus, isPending: isInstructionPending } = useUpdateInstructionStatus()

	const onChangeSwitecherHandle = (questionId: number, checked: boolean) => {
		setPendingQuestionId(questionId)
		updateInstructionsStatus(
			{ questionId: questionId, state: checked === true ? "active" : "inactive" },
			{
				onError: () => {
					showErrorMessage("Something went wrong, please try again later")
					setPendingQuestionId(null)
				},
				onSuccess: async () => {
					await queryClient.invalidateQueries({ queryKey: ["getInstructions"] })
					setPendingQuestionId(null)
				},
			},
		)
	}

	const dropdownHandle = (item: IInstructionsTable) => {
		const questionType = item.question_type
		const isSeriesOrBidRequest = questionType === "series_extraction" || questionType === "bid_request"

		const dropdownItems: MenuProps["items"] = [
			{
				key: "edit",
				label: "Edit",
				onClick: () => {
					setInstruction({
						questionId: item.id,
						question: item.question,
						sections: item.sections,
						prompt: item.prompt,
					})
					setEditInstructionModalOpen?.()
				},
			},
		]

		// Disable delete for series extraction and bid request instructions
		if (!isSeriesOrBidRequest) {
			dropdownItems.push(
				{
					type: "divider",
				},
				{
					key: "delete",
					label: "Delete",
					danger: true,
					onClick: () => onDeleteClick(item.id),
				},
			)
		}

		return dropdownItems
	}

	const checkInstructionPending = (questionId: number) => {
		return (isInstructionPending || isInstructionsFetching) && pendingQuestionId === questionId
	}

	return { dropdownHandle, onChangeSwitecherHandle, checkInstructionPending }
}
